import { useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, useParams, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { overviewNumbers } from "../../components/e-accreditation/model";
import { usePageBar } from "../../components/shell/page-bar";
import { cardsSummaryOptions } from "../../lib/api/e-accreditation";
import { useDefaultGamesId } from "../../lib/competition/games";
import { canSeeActivationHistory, canSeePrintBatches } from "../../lib/e-accreditation/access";

/**
 * E-Accreditation (M7a slice 1) — the layout: her tab row over the page.
 *   host   Overview · Cards · Print batches · Activation history
 *   NOC    Overview · Cards (its own cards, scoped by the API)
 * The card page (`$cardId`) is a full page with the back arrow instead of
 * the tabs. Her Requests and Daily passes tabs belong to later slices.
 *
 * Who sees which tab: `lib/e-accreditation/access.ts` — a courtesy, not the
 * control: the API scopes every row in SQL and checks every write.
 */
export const Route = createFileRoute("/_app/accreditation/e-accreditation")({
	validateSearch: z.object({ games_id: z.string().uuid().optional().catch(undefined) }),
	staticData: { contentTop: "tabbed" },
	component: EAccreditationLayout,
});

type TabTo =
	| "/accreditation/e-accreditation"
	| "/accreditation/e-accreditation/cards"
	| "/accreditation/e-accreditation/print-batches"
	| "/accreditation/e-accreditation/history";
type Tab = { key: "overview" | "cards" | "printBatches" | "history"; to: TabTo; badge?: number; badgeHint?: string };

function EAccreditationLayout() {
	const { t } = useTranslation("eAccreditation");
	const { t: tn } = useTranslation("nav");
	const { formatNumber: fmt } = useUiStrings();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const params = useParams({ strict: false }) as { cardId?: string };
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const gamesId = useDefaultGamesId(search.games_id);
	// The Cards tab's badge: printed cards waiting for activation — the Overview's own query.
	const summary = useQuery(cardsSummaryOptions(gamesId));
	const host = canSeeActivationHistory(session);
	const toActivate = host && summary.data ? overviewNumbers(summary.data).toActivate : undefined;

	usePageBar({ subtitle: tn("sections.accreditation") });

	if (params.cardId !== undefined) return <Outlet />;

	const tabs: Tab[] = [
		{ key: "overview", to: "/accreditation/e-accreditation" },
		{
			key: "cards",
			to: "/accreditation/e-accreditation/cards",
			badge: toActivate,
			badgeHint: toActivate !== undefined ? t("tabs.toActivate", { count: toActivate, n: fmt(toActivate) }) : undefined,
		},
		...(canSeePrintBatches(session) ? [{ key: "printBatches", to: "/accreditation/e-accreditation/print-batches" } as const] : []),
		...(host ? [{ key: "history", to: "/accreditation/e-accreditation/history" } as const] : []),
	];
	const current = (to: TabTo) => (to === "/accreditation/e-accreditation" ? pathname === to || pathname === `${to}/` : pathname.startsWith(to));

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			{/* Narrow screens scroll the strip; a fade on the right edge says there is more (the padding lets the last tab clear it). */}
			<nav
				aria-label={t("tabs.label")}
				className="-mt-[0.25rem] flex gap-[0.25rem] overflow-x-auto border-b border-gms-line [scrollbar-width:none] max-[45rem]:pr-[2.5rem] max-[45rem]:[mask-image:linear-gradient(to_right,#000_calc(100%-2.5rem),transparent)]"
			>
				{tabs.map((tab) => (
					<Link
						key={tab.key}
						to={tab.to}
						search={(p: { games_id?: string }) => ({ games_id: p.games_id })}
						aria-current={current(tab.to) ? "page" : undefined}
						className={`relative inline-flex h-[2.75rem] shrink-0 items-center gap-[0.375rem] px-[0.75rem] text-[0.875rem] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
							current(tab.to)
								? "font-semibold text-gms-navy after:absolute after:inset-x-[0.5rem] after:-bottom-px after:h-[3px] after:rounded-t-[3px] after:bg-gms-red"
								: "font-medium text-gms-ink-2 hover:text-gms-navy"
						}`}
					>
						{t(`tabs.${tab.key}`)}
						{!!tab.badge && tab.badge > 0 && (
							<span
								title={tab.badgeHint}
								className="inline-grid h-[1.125rem] min-w-[1.25rem] place-items-center rounded-full bg-gms-red px-[0.3125rem] text-[0.6875rem] font-bold text-white tabular-nums"
							>
								{fmt(tab.badge)}
							</span>
						)}
					</Link>
				))}
			</nav>
			<Outlet />
		</div>
	);
}
